'use client';

import React, { useState, useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';

export interface VerifyEmailPageProps {
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: Record<string, string>;
}

const INVALID_TOKEN_MESSAGES = [
  'geçersiz doğrulama token',
  'invalid verification token',
  'invalid token',
  'geçersiz doğrulama linki',
  'this link is invalid or has expired',
];

function isInvalidTokenBackendMessage(text: string): boolean {
  const lower = text.toLowerCase().trim();
  return INVALID_TOKEN_MESSAGES.some((msg) => lower.includes(msg));
}

function VerifyEmailContent({ language }: { language?: string }) {
  const searchParams = useSearchParams();
  const { getVariable } = useLanguageVariables();
  const t = (key: string, fallback: string) => getVariable(key, fallback) ?? fallback;

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const requestSentRef = useRef(false);

  const token = searchParams?.get('token') ?? null;

  /** API'den gelen metni kullanıcıya gösterilir hale getirir; ham JSON ise çıkarır; site diline uygun çeviri kullanır. */
  const toDisplayMessage = (raw: string | undefined | null): string => {
    if (typeof raw !== 'string' || !raw.trim()) {
      return t('auth.verify_email.invalid_token', 'This link is invalid or has expired.');
    }
    const trimmed = raw.trim();
    if (trimmed.startsWith('{') && trimmed.includes('"')) {
      try {
        const parsed = JSON.parse(trimmed) as { error?: string; message?: string };
        const msg = parsed?.error ?? parsed?.message;
        const str = typeof msg === 'string' && msg.trim() ? msg.trim() : '';
        return isInvalidTokenBackendMessage(str) ? t('auth.verify_email.invalid_token', 'This link is invalid or has expired.') : (str || t('auth.verify_email.invalid_token', 'This link is invalid or has expired.'));
      } catch {
        return t('auth.verify_email.invalid_token', 'This link is invalid or has expired.');
      }
    }
    if (trimmed.includes(' - {') && trimmed.includes('"')) {
      const afterDash = trimmed.split(' - ').pop() ?? '';
      try {
        const parsed = JSON.parse(afterDash) as { error?: string; message?: string };
        const msg = parsed?.error ?? parsed?.message;
        const str = typeof msg === 'string' && msg.trim() ? msg.trim() : trimmed;
        return isInvalidTokenBackendMessage(str) ? t('auth.verify_email.invalid_token', 'This link is invalid or has expired.') : str;
      } catch {
        return isInvalidTokenBackendMessage(trimmed) ? t('auth.verify_email.invalid_token', 'This link is invalid or has expired.') : trimmed;
      }
    }
    if (isInvalidTokenBackendMessage(trimmed)) {
      return t('auth.verify_email.invalid_token', 'This link is invalid or has expired.');
    }
    return trimmed;
  };

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('auth.verify_email.missing_token', 'Invalid link. Token is required.'));
      return;
    }

    if (requestSentRef.current) {
      return;
    }
    requestSentRef.current = true;

    setStatus('loading');
    setMessage(t('auth.verify_email.verifying', 'Verifying your email...'));

    const apiUrl = new URL(`/api/customer/auth/verify-email/${encodeURIComponent(token)}`, window.location.origin);
    if (language) {
      apiUrl.searchParams.set('language', language);
    }

    fetch(apiUrl.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success) {
          setStatus('success');
          setMessage(t('auth.verify_email.success', 'Your email has been verified. You can now log in.'));
        } else {
          setStatus('error');
          setMessage(toDisplayMessage(data?.error ?? data?.message) || t('auth.verify_email.invalid_token', 'This link is invalid or has expired.'));
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage(t('auth.verify_email.error', 'Something went wrong. Please try again later.'));
      });
  }, [token]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-neutral-200/90 p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-alsero-first/10 items-center justify-center mb-4">
              <div className="w-8 h-8 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('auth.verify_email.title', 'Verify your email')}
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-green-100 items-center justify-center mb-4">
              <CheckCircleIcon className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('auth.verify_email.success_title', 'Email verified')}
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link
              href="/login"
              className="inline-block px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('auth.login.form.submit', 'Login')}
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-amber-100 items-center justify-center mb-4">
              <ExclamationTriangleIcon className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('auth.verify_email.error_title', 'Verification failed')}
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function VerifyEmailPage({ language, translations }: VerifyEmailPageProps) {
  return (
    <Suspense
      fallback={
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      <VerifyEmailContent language={language} />
    </Suspense>
  );
}
